import React, { useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { Outlet } from '@tanstack/react-router';
import { Sidebar } from './Sidebar';
import { FilterSidebar as InventoryFilterSidebar } from '@/features/inventory/components/FilterSidebar';
import { logger } from '@/utils/logger';
import { FilterSidebar as AvailabilityFilterSidebar } from '@/features/availability/components/FilterSidebar';
import { FilterSidebar as TradesFilterSidebar } from '@/features/trades/components/FilterSidebar';
import { MtmFilterSidebar } from '@/features/trades/components/mtm/MtmFilterSidebar';
import { MtmCrossDndProvider } from '@/features/trades/components/mtm/MtmCrossDndContext';
import { FilterSidebar as CounterpartyFilterSidebar } from '@/features/counterparties/components/FilterSidebar';
import { FilterSidebar as RecallReportFilterSidebar } from '@/features/recall-report/components/FilterSidebar';
import { AvailabilityFilterSidebar as CounterpartyAvailabilityFilterSidebar } from '@/features/counterparty-reports/components/AvailabilityFilterSidebar';
import { CrossDndProvider } from '@/features/inventory/components/CrossDndContext';
import { TradesCrossDndProvider } from '@/features/trades/components/TradesCrossDndContext';
import { AvailabilityCrossDndProvider } from '@/features/availability/components/AvailabilityCrossDndContext';
import { CreateTradeModal } from '@/features/trades/components';
import { TourProvider, WhatsNewBanner } from '@/features/onboarding';
import { Toast } from '@/components/ui/Toast';
import { useUIStore, useInventoryFilterStore, useInventoryDataStore, useAvailabilityStore, useTradesDataStore, useAuthStore } from '@/stores';
import { useGlobalSettings } from '@/hooks';
import { useInventoryStates } from '@/hooks';
import { useFxRatesSync, useCobFxRatesSync } from '@/hooks/fx-rates';
import { exportAvailabilityToCSV } from '@/utils/inventory/csv-export';
import { isInventoryPath, mergeInventorySearch } from '@/features/inventory/router-helpers';
import { mergeTradesSearch } from '@/features/trades/router-helpers';

interface LayoutProps {
  children?: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children: _children }) => {
  const location = useLocation();
  const sidebarCollapsed = useUIStore((state) => state.sidebarCollapsed);
  const filterSidebarCollapsed = useUIStore((state) => state.filterSidebarCollapsed);
  const showFilterSidebar = useUIStore((state) => state.showFilterSidebar);
  const filterMetadata = useInventoryFilterStore((state) => state.filterMetadata);
  const filteredInventoryData = useInventoryDataStore((state) => state.filteredInventoryData);
  const currentInventoryStateName = useInventoryDataStore((state) => state.currentInventoryStateName);
  const inventoryDataLoading = useInventoryDataStore((state) => state.inventoryDataLoading);
  const inventoryDataError = useInventoryDataStore((state) => state.inventoryDataError);
  const activeInventoryTab = useInventoryDataStore((state) => state.activeInventoryTab);
  const filteredAvailabilityData = useAvailabilityStore((state) => state.filteredAvailabilityData || []);
  const availabilityFilters = useAvailabilityStore((state) => state.availabilityFilters);
  const { fetchGlobalSettings } = useGlobalSettings();
  // Sync spot FX rates into the currency-service module state app-wide. The
  // trade form also calls this, but only while open — without an app-root mount
  // the Trades/MTM currency totals would render on stale fallback rates until a
  // user happened to open the trade modal. Side-effect only here (LR-469).
  useFxRatesSync();
  // LR-493: same app-root mount for the COB (T-1) rates behind the Run-MTM "Delta ≈" target preview
  // and the repo margin-call "Pay ≈" transfer equivalent — a separate feed from the live spot above
  // (the trade-entry form keeps using the live feed, not this COB one).
  useCobFxRatesSync();
  const { data: statesData, isLoading: isStatesLoading } = useInventoryStates();
  const isInventoryPage = isInventoryPath(location.pathname);
  const isTradesPage = location.pathname === '/trades' || location.pathname === '/trades/';
  const searchParams = (location.search ?? {}) as Record<string, string | undefined>;
  // The MTM workspace needs its OWN sidebar (mtm column + filter stores) instead of
  // the Trades one (LR-394). LR-587 moved it to Collateral Manager: the GMSLA MTM
  // subtab, and the repo margin call under Cash Pools -> GMRA. Both sidebars are the
  // same width, so the right padding on <main> below stays keyed on the pages that
  // claim one.
  const isCollateralPage =
    location.pathname === '/collateral' || location.pathname === '/collateral/';

  const isCounterpartiesPage = location.pathname === '/counterparties' || location.pathname === '/counterparties/';
  const isCounterpartyReportsTab = isInventoryPage && activeInventoryTab === 'counterparty-reports';
  const filteredTradesData = useTradesDataStore((state) => state.filteredTradesData);
  const tradesDataLoading = useTradesDataStore((state) => state.isTradesDataLoading);
  const tradesDataError = useTradesDataStore((state) => state.tradesDataError);
  const reportTab = searchParams.reportTab;
  const cpFromUrl = searchParams.cp || 'velocity';
  const crossDndEnabled = isInventoryPage && showFilterSidebar && activeInventoryTab === 'inventory';
  const availabilityCrossDndEnabled = !crossDndEnabled && isInventoryPage && showFilterSidebar && activeInventoryTab === 'availability';
  const tradesCrossDndEnabled = !crossDndEnabled && !availabilityCrossDndEnabled && isTradesPage && showFilterSidebar;
  const mtmCrossDndEnabled = !crossDndEnabled && !availabilityCrossDndEnabled && isCollateralPage && showFilterSidebar;
  const authUser = useAuthStore((state) => state.user);
  const currentUserEmail = authUser?.email ?? null;

  const navigate = useNavigate();
  // Keep the URL in sync with the selected saved view so deep-links (and a
  // plain F5 reload) naturally land back on the same view. `replace: true`
  // — every dropdown click would otherwise churn browser history.
  const handleInventoryActiveViewChange = useCallback(
    (viewId: number | null) => {
      navigate({
        to: '/inventory',
        search: (prev) => mergeInventorySearch(prev, { view_id: viewId ?? undefined }),
        replace: true,
      });
    },
    [navigate],
  );

  // LR-671: same contract for the Trades saved views. `view_id` is patched over
  // the previous search so the counterparty tab, mode and History params survive.
  const handleTradesActiveViewChange = useCallback(
    (viewId: number | null) => {
      navigate({
        to: '/trades',
        search: (prev) => mergeTradesSearch(prev, { view_id: viewId ?? undefined }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleAvailabilityExport = useCallback(() => {
    const excludeInternalColumns = availabilityFilters?.excludeInternalColumns ?? true;
    exportAvailabilityToCSV(filteredAvailabilityData, currentInventoryStateName, excludeInternalColumns);
  }, [filteredAvailabilityData, currentInventoryStateName, availabilityFilters]);

  // Fetch global settings on mount
  useEffect(() => {
    fetchGlobalSettings().catch(logger.error);
  }, [fetchGlobalSettings]);

  return (
    <TourProvider>
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <CrossDndProvider enabled={crossDndEnabled}>
      {isInventoryPage && showFilterSidebar && activeInventoryTab === 'inventory' && (
        <InventoryFilterSidebar
          currentUserEmail={currentUserEmail}
          onActiveViewChange={handleInventoryActiveViewChange}
          accountIds={filterMetadata.accountIds}
          counterparties={filterMetadata.counterparties}
          currencies={filterMetadata.currencies}
          caStatuses={filterMetadata.caStatuses}
          quantityRange={filterMetadata.quantityRange}
          quantityT1Range={filterMetadata.quantityT1Range}
          quantityT2Range={filterMetadata.quantityT2Range}
          quantityTxRange={filterMetadata.quantityTxRange}
          leverageRateRange={filterMetadata.leverageRateRange}
          valueEURRange={filterMetadata.valueEURRange}
          currencyBenchmarkRange={filterMetadata.currencyBenchmarkRange}
          dailyPotentialRange={filterMetadata.dailyPotentialRange}
          adtv30DRange={filterMetadata.adtv30DRange}
          adtvMultipleRange={filterMetadata.adtvMultipleRange}
          adtvAdjustedRange={filterMetadata.adtvAdjustedRange}
          benchmarkFeeRange={filterMetadata.benchmarkFeeRange}
          activeUtilisationRange={filterMetadata.activeUtilisationRange}
          quantityOnLoanRange={filterMetadata.quantityOnLoanRange}
          rebateRateRange={filterMetadata.rebateRateRange}
          filteredData={filteredInventoryData}
          currentStateName={currentInventoryStateName}
          statesData={statesData}
          isStatesLoading={isStatesLoading}
          isDataLoading={inventoryDataLoading}
          hasError={inventoryDataError}
        />
      )}
      <AvailabilityCrossDndProvider enabled={availabilityCrossDndEnabled}>
      {isInventoryPage && showFilterSidebar && activeInventoryTab === 'availability' && (
        <AvailabilityFilterSidebar
          counterparties={filterMetadata.counterparties}
          caStatuses={filterMetadata.caStatuses}
          filteredDataCount={filteredAvailabilityData.length}
          currentStateName={currentInventoryStateName}
          statesData={statesData}
          isStatesLoading={isStatesLoading}
          isDataLoading={inventoryDataLoading}
          hasError={inventoryDataError}
          onExport={handleAvailabilityExport}
        />
      )}
      <TradesCrossDndProvider enabled={tradesCrossDndEnabled}>
      <MtmCrossDndProvider enabled={mtmCrossDndEnabled}>
      {isTradesPage && showFilterSidebar && (
        <TradesFilterSidebar
          currentUserEmail={currentUserEmail}
          onActiveViewChange={handleTradesActiveViewChange}
          filteredDataCount={filteredTradesData.length}
          isDataLoading={tradesDataLoading}
          hasError={tradesDataError}
          isHistoryMode={searchParams.mode === 'history'}
        />
      )}
      {isCollateralPage && showFilterSidebar && (
        <MtmFilterSidebar
          isDataLoading={tradesDataLoading}
          hasError={tradesDataError}
        />
      )}
      {isCounterpartiesPage && showFilterSidebar && (
        <CounterpartyFilterSidebar />
      )}
      {isCounterpartyReportsTab && showFilterSidebar && reportTab === 'recall' && (
        <RecallReportFilterSidebar />
      )}
      {isCounterpartyReportsTab && showFilterSidebar && reportTab !== 'recall' && (
        <CounterpartyAvailabilityFilterSidebar counterparty={cpFromUrl} />
      )}
      {/* The route conjunct below is load-bearing: showFilterSidebar defaults to true
          and the rail-less routes (/billing, /trades/$tradeId, /) never clear it.
          useShellInsets — which places the floating bars and the scroll-to-top
          button — mirrors the flag alone, so it is only valid on the routes listed
          here. /collateral moved into the list with the MTM rail (LR-587). */}
      <main
        className={`
          transition-all duration-300
          ${sidebarCollapsed ? 'pl-18' : 'pl-64'}
          ${(isInventoryPage || isTradesPage || isCounterpartiesPage || isCollateralPage) && showFilterSidebar ? (filterSidebarCollapsed ? 'pr-18' : 'pr-72') : ''}
        `}
      >
        <div className="mx-auto h-full">
          <Outlet />
        </div>
      </main>
      </MtmCrossDndProvider>
      </TradesCrossDndProvider>
      </AvailabilityCrossDndProvider>
      </CrossDndProvider>
      <CreateTradeModal />
      <WhatsNewBanner />
      <Toast />
    </div>
    </TourProvider>
  );
};
